import { useEffect, useState } from "react";
import api from "services/api";
import { useLanguage } from "context/LanguageContext";
import { useSnackbar } from "context/SnackbarContext";

// Loads the given fields of one company and saves only them (company_update accepts partial data).
export default function useCompanyForm(companyId, defaults) {
  const { t } = useLanguage();
  const showSnackbar = useSnackbar();
  const [initial, setInitial] = useState(null);
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setInitial(null);
    setValues(null);
    api
      .get("/api/companies/")
      .then((response) => {
        const company = (response.data.companies || []).find((c) => c.id === companyId);
        if (cancelled || !company) return;
        const loaded = Object.keys(defaults).reduce((acc, key) => {
          const value = company[key];
          acc[key] = value === null || value === undefined ? defaults[key] : value;
          return acc;
        }, {});
        setInitial(loaded);
        setValues(loaded);
      })
      .catch(() => showSnackbar(t("stLoadFailed"), "error"));
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const dirty =
    !!values && !!initial && Object.keys(values).some((key) => values[key] !== initial[key]);

  const setField = (key) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setValues((previous) => ({ ...previous, [key]: value }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/api/company/${companyId}/update/`, values);
      setInitial(values);
      showSnackbar(t("stSaved"), "success");
      return true;
    } catch (error) {
      const errors = error.response?.data?.errors;
      const details = errors ? Object.values(errors).flat().join(" ") : error.response?.data?.error;
      showSnackbar(details ? `${t("stSaveFailed")}: ${details}` : t("stSaveFailed"), "error");
      return false;
    } finally {
      setSaving(false);
    }
  };

  return { values, dirty, saving, setField, save };
}
